/**
 * ProfitLossService - frontend wrapper for the accounting Profit & Loss route.
 * Returns monthly income (outbound invoices) minus expenses (inbound invoices +
 * expense claims), oldest -> newest. Same source the accounting feature uses.
 */

import { API_BASE, getAuthHeaders } from '../utils/supabase/client';
import type { ProfitLossMonth } from '../shared-ui/home/homeModels';

// Declared with the shared Home models so the native app can use it.
export type { ProfitLossMonth };

// ─── Local cache (stale-while-revalidate) ────────────────────────────────────
// The P&L card sits beside cards backed by the persistent DB cache, so they
// render instantly. This gives P&L the same cache-first behaviour: the last
// fetched series is stored per workspace+window so the card paints immediately
// on return visits and only shows "Loading..." on the genuine first load.

const CACHE_KEY = 'quantis_profit_loss_cache_v1';

type ProfitLossCacheMap = Record<string, ProfitLossMonth[]>;

function cacheSlot(workspaceId: string, months: number): string {
  return `${workspaceId}:${months}`;
}

function readCacheMap(): ProfitLossCacheMap {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as ProfitLossCacheMap) : {};
  } catch {
    return {};
  }
}

export const ProfitLossService = {
  /** Last-known monthly series for this workspace, read synchronously for an
   *  instant first paint. Null when nothing is cached yet. */
  getCachedMonthly(workspaceId: string, months = 6): ProfitLossMonth[] | null {
    const rows = readCacheMap()[cacheSlot(workspaceId, months)];
    // A cached empty array is a real "no activity" hit, not a miss — returning it
    // lets a no-activity workspace paint its empty state instantly instead of
    // flashing "Loading..." and refetching on every visit.
    return Array.isArray(rows) ? rows : null;
  },

  async getMonthly(workspaceId: string, months = 6): Promise<ProfitLossMonth[]> {
    const headers = await getAuthHeaders();
    const res = await fetch(
      `${API_BASE}/profit-loss?workspaceId=${encodeURIComponent(workspaceId)}&months=${months}`,
      { headers },
    );
    if (!res.ok) {
      let message = `HTTP ${res.status}`;
      try {
        const data = await res.json();
        message = (data && (data.error || data.message)) || message;
      } catch {
        /* non-JSON error body */
      }
      throw new Error(message);
    }
    const data = (await res.json()) as ProfitLossMonth[];
    try {
      const map = readCacheMap();
      map[cacheSlot(workspaceId, months)] = data;
      localStorage.setItem(CACHE_KEY, JSON.stringify(map));
    } catch {
      /* cache write is best-effort */
    }
    return data;
  },
};
